import { useState, useEffect, useCallback } from 'react';
import { generateQRCodeDataURL, QRCodeOptions } from './utils';

export const useGenerateQRCode = (
  email: string,
  options: QRCodeOptions = {},
  onGenerated?: (dataURL: string) => void,
  onError?: (error: string) => void
): UseGenerateQRCodeResult => {
  const [qrDataURL, setQrDataURL] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateQRCode = useCallback(async () => {
    if (!email) {
      setQrDataURL(null);
      const errorMsg = 'No email provided for QR code generation';
      setError(errorMsg);
      onError?.(errorMsg);
      setIsGenerating(false);
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const dataURL = await generateQRCodeDataURL(email, options);
      setQrDataURL(dataURL);
      onGenerated?.(dataURL);
    } catch (error) {
      const errorMsg = typeof error === 'string' ? error : 'Failed to generate QR code';
      setError(errorMsg);
      onError?.(errorMsg);
      setQrDataURL(null);
    } finally {
      setIsGenerating(false);
    }
  }, [email, options, onGenerated, onError]);

  useEffect(() => {
    generateQRCode();
  }, [generateQRCode]);

  return {
    qrDataURL,
    isGenerating,
    error,
  };
};

interface UseGenerateQRCodeResult {
  qrDataURL: string | null;
  isGenerating: boolean;
  error: string | null;
}
